import { Store } from 'redux';

import { ACTIONS } from '../../constants';
import { logChatbotStartOver } from '../bi';
import { sendRestartChat } from '../common/api/sendRestartChat';
import { TRootState } from '../common/reducers';
import { chatActions } from '../common/reducers/chat';
import { IMetaUpdateCopilotChatbotVisible, IMetaUpdatePageUrl, ResetReason } from '../common/reducers/types';
import { selectItems, selectRestartChatParameters } from '../common/selectors/chat';
import { selectLoadPageUrl } from '../common/selectors/meta';
import { selectSelectedSite } from '../common/selectors/sites';

type UnsubscribeCallback = () => void;

const trustedSourcesRegexp =
  /^(https:\/\/support\.wix\.com|https:\/\/(.+)\.support\.com|https?:\/\/localhost(:\d+)?)$/i;

type CopilotMessageData = {
  type: string;
  event: string;
  query: string;
  loadPageUrl: string;
  referral: string;
  isVisible: boolean;
  payload: Record<string, any>;
  nodeId?: string;
};

const createOnMessageHandler =
  (sendMessageCallback: (message: string) => void, store: Store<TRootState>) =>
  ({ origin, data, source }: MessageEvent<CopilotMessageData>): void => {
    const isTrustedSources = trustedSourcesRegexp.test(origin);
    const isCopilotType = data && data.type === 'COPILOT_CHATBOT';

    if ((!isTrustedSources && source !== window) || !isCopilotType) {
      return;
    }

    const { event, query, loadPageUrl, referral, isVisible, nodeId } = data;
    switch (event) {
      case 'init_copilot_params':
        if (loadPageUrl) {
          store.dispatch<IMetaUpdatePageUrl>({
            type: ACTIONS.META_UPDATE_PAGE_URL,
            loadPageUrl,
            startUrl: window.location.href,
            referral,
            isCopilotInitialized: true,
          });
          store.dispatch(
            chatActions.setStartChatParameters({
              referral,
              loadPageUrl,
              ...(nodeId && { nodeId }),
            }),
          );
          store.dispatch(
            chatActions.changeRestartChatParameters({
              ...store.getState().chat.restartChatParameters,
              referral,
              ...(nodeId && { nodeId }),
            }),
          );
        }
        if (referral) {
          sessionStorage.setItem('referral', referral);
        }
        break;
      case 'pre_message_send':
        const items = selectItems(store.getState());
        if (items.length === 0) {
          sendMessageCallback(query);
        } else {
          const restartParameters = selectRestartChatParameters(store.getState());
          chatActions.reset(ResetReason.StartOver);
          logChatbotStartOver(store.getState());
          const loadPageUrlFromState = selectLoadPageUrl(store.getState());
          const editorType = selectSelectedSite(store.getState())?.editorType;
          sendRestartChat({...restartParameters, nodeId: undefined}, loadPageUrlFromState, editorType)
            .then(() => sendMessageCallback(query))
            .catch(() => sendMessageCallback(query));
        }
        break;
      case 'init_chat_flow_for_wrong_article':
        const restartParameters = selectRestartChatParameters(store.getState());
        chatActions.reset(ResetReason.StartOver);
        logChatbotStartOver(store.getState());
        const loadPageUrlFromState = selectLoadPageUrl(store.getState());
        const editorType = selectSelectedSite(store.getState())?.editorType;
        sendRestartChat({ ...restartParameters, nodeId }, loadPageUrlFromState, editorType);
        break;
      case 'is_copilot_chatbot_visible':
        store.dispatch<IMetaUpdateCopilotChatbotVisible>({
          type: ACTIONS.META_UPDATE_COPILOT_CHATBOT_VISIBLE,
          isCopilotChatbotVisible: isVisible,
        });
        break;
      default:
        throw new Error(`Unknown event: ${event}`);
    }
  };

export const subscribeCopilotToMessages = (
  store: Store<TRootState>,
  sendMessageCallback: (message: string) => void,
): UnsubscribeCallback => {
  const onMessage = createOnMessageHandler(sendMessageCallback, store);
  window.addEventListener('message', onMessage);

  return () => window.removeEventListener('message', onMessage);
};
